import { useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { isEqualTrackRef } from '@livekit/components-core'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { useTracks, useVisualStableUpdate } from '@livekit/components-react'
import { Track } from 'livekit-client'

import { useMeetingSounds } from 'modules/meetings/lib/hooks/useMeetingSounds'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'
import { useVirtualBackground } from 'modules/meetings/lib/hooks/useVirtualBackground'
import { getTrackPositions } from 'modules/meetings/lib/utils/getTrackPositions'

import { ParticipantTile } from './components/ParticipantTile'

export const Grid = () => {
  const itemPerPage = 6

  const initialTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  )

  // speaking participants move to the first page and are always visible if there is overflow
  const tracks = useVisualStableUpdate(initialTracks, itemPerPage)

  // Get sounds enabled state from room context
  const { soundsEnabled } = useRoomContext()

  // Use meeting sounds hook for join/leave notifications
  useMeetingSounds(soundsEnabled)

  // Use virtual background hook
  useVirtualBackground()

  const [focusedTrack, setFocusedTrack] = useState<TrackReferenceOrPlaceholder | undefined>(
    undefined
  )

  // Keep track of auto-focused tracks to avoid re-focusing
  const autoFocusedTracksRef = useRef<Set<string>>(new Set())

  const containerRef = useRef<HTMLDivElement>(null)
  const [isSmallMode, setIsSmallMode] = useState(false)
  const [isMediumMode, setIsMediumMode] = useState(false)

  useEffect(() => {
    if (!containerRef.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width
        setIsSmallMode(width < 768)
        setIsMediumMode(width < 1024)
      }
    })
    resizeObserver.observe(containerRef.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Unified auto-focus logic for both screen shares and participants
  useEffect(() => {
    const screenShareTracks = tracks.filter((track) => track.source === Track.Source.ScreenShare)
    const cameraTracks = tracks.filter((track) => track.source === Track.Source.Camera)

    let trackToFocus: TrackReferenceOrPlaceholder | undefined

    // Priority 1: Auto-focus new screen share tracks
    if (screenShareTracks.length > 0) {
      trackToFocus = screenShareTracks.find((track) => {
        const trackId = `${track.participant?.identity}-${track.source}`
        return !autoFocusedTracksRef.current.has(trackId)
      })
    }
    // Priority 2: Auto-focus remote participant when there are exactly 2 camera tracks and no screen sharing
    else if (cameraTracks.length === 2) {
      const remoteParticipantTrack = cameraTracks.find((track) => !track.participant?.isLocal)

      if (remoteParticipantTrack) {
        const trackId = `${remoteParticipantTrack.participant?.identity}-${remoteParticipantTrack.source}`

        // Only auto-focus if this participant hasn't been auto-focused before
        // and there's no current focus or the current focus is not a screen share
        if (
          !autoFocusedTracksRef.current.has(trackId) &&
          (!focusedTrack || focusedTrack.source !== Track.Source.ScreenShare)
        ) {
          trackToFocus = remoteParticipantTrack
        }
      }
    }

    // Apply the focus if a track was selected
    if (trackToFocus) {
      setFocusedTrack(trackToFocus)
      const trackId = `${trackToFocus.participant?.identity}-${trackToFocus.source}`
      autoFocusedTracksRef.current.add(trackId)
    }

    // Clean up auto-focused tracking for tracks that no longer exist
    const currentTrackIds = new Set(
      tracks.map((track) => `${track.participant?.identity}-${track.source}`)
    )

    const newAutoFocusedTracks = new Set<string>()
    autoFocusedTracksRef.current.forEach((id) => {
      if (currentTrackIds.has(id)) {
        newAutoFocusedTracks.add(id)
      }
    })
    autoFocusedTracksRef.current = newAutoFocusedTracks
  }, [tracks, focusedTrack])

  useEffect(() => {
    if (!focusedTrack) return

    const trackStillExists = tracks.some((track) => isEqualTrackRef(track, focusedTrack))
    const lessThan2Tracks = tracks.length < 2
    if (!trackStillExists || lessThan2Tracks) {
      setFocusedTrack(undefined)
    }
  }, [tracks, focusedTrack])

  const handleTrackFocus = (track: TrackReferenceOrPlaceholder) => {
    setFocusedTrack(track)
  }

  const handleTrackUnfocus = () => {
    setFocusedTrack(undefined)
  }

  const trakcsToRender = tracks.map((track, index) => {
    const participant = track.participant
    if (!participant) return null
    return (
      <ParticipantTile
        key={`${track.participant?.identity || 'local'}-${track.source || 'unknown'}-${index}`}
        trackRef={track}
        participant={participant}
        numberOfTracks={tracks.length}
        onFocus={() => handleTrackFocus(track)}
        onUnfocus={handleTrackUnfocus}
        isFocused={focusedTrack ? isEqualTrackRef(track, focusedTrack) : false}
      />
    )
  })

  const focusedTrackIndex = focusedTrack
    ? tracks.findIndex((track) => isEqualTrackRef(track, focusedTrack))
    : undefined

  const positions = getTrackPositions(tracks.length, isSmallMode, isMediumMode, focusedTrackIndex)

  return (
    <Box ref={containerRef} position="relative" style={{ width: '100%' }} height="full">
      {positions.map((position, index) => (
        <Box
          key={index}
          position="absolute"
          style={{
            ...position,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {trakcsToRender[index]}
        </Box>
      ))}
    </Box>
  )
}
